class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    let intersected = [];

    btn.addEventListener("click", (e) => {
      if (!addToCart) {
        e.preventDefault();
      }
      addToCartModule.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      if (intersected.length) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

class PdpSellingPlanFilter {
  constructor() {
    this.appId = "5859381";
    this.singlePlanGroupId = "cmjsqyn5t1cgkxf3i4zyuzzgw";
    this.bundlePlanGroupId = "cmkfntw630aav1zkr0o0jgpbl";
    this.activePlanGroupId = this.singlePlanGroupId;
    this.singleAllowedFrequencyLabels = new Set([
      "monthly",
      "month",
      "every month",
      "every 4 weeks",
      "delivery every 4 weeks",
      "every 2 months",
      "every 2 month",
      "2 months",
      "2 month",
      "bi monthly",
      "bimonthly",
      "bi month",
      "every 8 weeks",
      "delivery every 8 weeks",
    ]);
    this.bundleParentHandles = new Set([
      "avanelle-cognitive-prime",
      "creatine-gummies-container-active",
      "creatine-gummies-container-elite",
    ]);
    this.isApplying = false;
    this.scheduled = false;
    this.fetchPatched = false;
    this.xhrPatched = false;
    this.allowedSellingPlanIds = new Set();
    this.contextReady = false;
  }

  async init() {
    if (!this.shouldRun()) return;
    await this.resolveContextFromCart();
    this.patchFetch();
    this.patchXhr();
    this.apply();
    this.preloadAllowedSellingPlanIds();
    this.observe();
  }

  shouldRun() {
    if (!window.location.pathname.includes("/products/")) return false;
    return Boolean(
      document.querySelector("section.wt-product__wrapper[data-product-handle]"),
    );
  }

  normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  normalizeGroupIdentifier(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  isAllowedFrequencyOption(optionText) {
    const normalized = this.normalizeText(optionText);
    if (
      this.normalizeGroupIdentifier(this.activePlanGroupId) ===
      this.normalizeGroupIdentifier(this.bundlePlanGroupId)
    ) {
      return true;
    }
    return this.singleAllowedFrequencyLabels.has(normalized);
  }

  isBundleParentProperty(value) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return (
      normalized === "true" ||
      normalized === "1" ||
      normalized === "yes" ||
      normalized === "on"
    );
  }

  getLineHandle(lineItem) {
    const handle =
      lineItem?.handle ||
      lineItem?.product_handle ||
      String(lineItem?.url || "")
        .split("?")[0]
        .match(/\/products\/([^/]+)/i)?.[1] ||
      "";
    return String(handle || "")
      .trim()
      .toLowerCase();
  }

  isBundleParentLine(lineItem) {
    if (!lineItem || typeof lineItem !== "object") return false;
    const properties = lineItem.properties || {};
    if (this.isBundleParentProperty(properties?._bundle_parent)) return true;
    const bundleId = String(properties?._bundle_id || "").trim();
    const handle = this.getLineHandle(lineItem);
    if (!bundleId) {
      return Boolean(handle && this.bundleParentHandles.has(handle));
    }
    if (
      String(properties?._bundle_min || "").trim() !== "" ||
      String(properties?._bundle_max || "").trim() !== ""
    ) {
      return true;
    }
    return Boolean(handle && this.bundleParentHandles.has(handle));
  }

  async resolveContextFromCart() {
    const root = window?.Shopify?.routes?.root || "/";
    const cartUrl = `${root}cart.js?_=${Date.now()}`;
    try {
      const response = await window.fetch(cartUrl, {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok) return;
      const cart = await response.json();
      const hasBundleParent = Array.isArray(cart?.items)
        ? cart.items.some((item) => this.isBundleParentLine(item))
        : false;
      this.activePlanGroupId = hasBundleParent
        ? this.bundlePlanGroupId
        : this.singlePlanGroupId;
      this.contextReady = true;
    } catch (error) {}
  }

  getGroupIdentifier(group) {
    const candidateValues = [
      group?.id,
      group?.selling_plan_group_id,
      group?.external_id,
      group?.app_id,
      group?.appId,
    ];
    const normalizedTarget = this.normalizeGroupIdentifier(this.activePlanGroupId);
    for (const value of candidateValues) {
      const normalizedValue = this.normalizeGroupIdentifier(value);
      if (!normalizedValue || !normalizedTarget) continue;
      if (
        normalizedValue.includes(normalizedTarget) ||
        normalizedTarget.includes(normalizedValue)
      ) {
        return String(value || "").trim();
      }
    }
    return String(group?.id || group?.selling_plan_group_id || "").trim();
  }

  shouldProcessUrl(url) {
    if (!url) return true;
    const normalizedUrl = String(url).toLowerCase();
    return (
      normalizedUrl.includes("/products") ||
      normalizedUrl.includes("selling_plan") ||
      normalizedUrl.includes("subscription") ||
      normalizedUrl.includes("/apps/") ||
      normalizedUrl.includes("graphql")
    );
  }

  isBundleGroup(group) {
    const normalizedName = this.normalizeText(group?.name || "");
    return normalizedName.includes("bundle");
  }

  isSingleGroup(group) {
    const normalizedName = this.normalizeText(group?.name || "");
    return normalizedName.includes("single");
  }

  getNormalizedPlanFrequencyLabel(plan) {
    if (!plan || typeof plan !== "object") return "";
    let label = String(plan?.name || "");
    if (Array.isArray(plan?.options)) {
      for (const option of plan.options) {
        const optionName = this.normalizeText(option?.name || "");
        if (optionName === "frequency") {
          label = String(option?.value || label);
          break;
        }
      }
    }
    return this.normalizeText(label);
  }

  isLikelySingleFrequencyGroup(group) {
    if (!group || !Array.isArray(group?.selling_plans)) return false;
    const normalizedLabels = group.selling_plans
      .map((plan) => this.getNormalizedPlanFrequencyLabel(plan))
      .filter(Boolean);
    if (!normalizedLabels.length) return false;
    return normalizedLabels.every((label) =>
      this.singleAllowedFrequencyLabels.has(label),
    );
  }

  groupMatchesActiveContext(group) {
    const normalizedTarget = this.normalizeGroupIdentifier(this.activePlanGroupId);
    if (!normalizedTarget) return false;
    const candidates = [
      group?.id,
      group?.selling_plan_group_id,
      group?.external_id,
      group?.name,
      group?.app_id,
      group?.appId,
    ];
    return candidates.some((candidate) => {
      const normalizedCandidate = this.normalizeGroupIdentifier(candidate);
      if (!normalizedCandidate) return false;
      return (
        normalizedCandidate.includes(normalizedTarget) ||
        normalizedTarget.includes(normalizedCandidate)
      );
    });
  }

  updateAllowedSellingPlanIdsFromGroups(groups) {
    const ids = new Set();

    groups.forEach((group) => {
      if (!Array.isArray(group?.selling_plans)) return;
      group.selling_plans.forEach((plan) => {
        const id = String(plan?.id ?? "");
        if (id) ids.add(id);
      });
    });

    this.allowedSellingPlanIds = ids;
  }

  selectSellingPlanGroups(groups) {
    if (!Array.isArray(groups) || groups.length === 0) return groups;
    const filteredByContext = groups.filter((group) =>
      this.groupMatchesActiveContext(group),
    );
    if (filteredByContext.length > 0) {
      this.updateAllowedSellingPlanIdsFromGroups(filteredByContext);
      return filteredByContext;
    }

    const isBundleContext =
      this.normalizeGroupIdentifier(this.activePlanGroupId) ===
      this.normalizeGroupIdentifier(this.bundlePlanGroupId);
    if (isBundleContext) {
      const bundleGroups = groups.filter((group) => this.isBundleGroup(group));
      if (bundleGroups.length > 0) {
        this.updateAllowedSellingPlanIdsFromGroups(bundleGroups);
        return bundleGroups;
      }
      const nonSingleFrequencyGroups = groups.filter(
        (group) => !this.isLikelySingleFrequencyGroup(group),
      );
      if (nonSingleFrequencyGroups.length > 0) {
        this.updateAllowedSellingPlanIdsFromGroups(nonSingleFrequencyGroups);
        return nonSingleFrequencyGroups;
      }
    } else {
      const singleGroups = groups.filter((group) => this.isSingleGroup(group));
      if (singleGroups.length > 0) {
        this.updateAllowedSellingPlanIdsFromGroups(singleGroups);
        return singleGroups;
      }
      const likelySingleGroups = groups.filter((group) =>
        this.isLikelySingleFrequencyGroup(group),
      );
      if (likelySingleGroups.length > 0) {
        this.updateAllowedSellingPlanIdsFromGroups(likelySingleGroups);
        return likelySingleGroups;
      }
      const nonBundleGroups = groups.filter((group) => !this.isBundleGroup(group));
      if (nonBundleGroups.length > 0) {
        this.updateAllowedSellingPlanIdsFromGroups(nonBundleGroups);
        return nonBundleGroups;
      }
    }

    this.updateAllowedSellingPlanIdsFromGroups(groups);
    return groups;
  }

  getProductHandle() {
    const section = document.querySelector(
      "section.wt-product__wrapper[data-product-handle]",
    );
    const handleFromAttr = section?.getAttribute("data-product-handle") || "";
    if (handleFromAttr) return handleFromAttr;

    const pathMatch = window.location.pathname.match(/\/products\/([^\/?#]+)/i);
    return pathMatch ? pathMatch[1] : "";
  }

  async preloadAllowedSellingPlanIds() {
    const handle = this.getProductHandle();
    if (!handle) return;

    const root = window?.Shopify?.routes?.root || "/";
    const url = `${root}products/${handle}.js`;

    try {
      const response = await window.fetch(url, { credentials: "same-origin" });
      if (!response.ok) return;

      const productPayload = await response.json();
      const groups = Array.isArray(productPayload?.selling_plan_groups)
        ? productPayload.selling_plan_groups
        : [];
      if (!groups.length) return;
      this.selectSellingPlanGroups(groups);
      this.apply();
    } catch (error) {}
  }

  filterPayload(payload) {
    if (!payload || typeof payload !== "object") {
      return { changed: false, data: payload };
    }

    let changed = false;
    let cloned = payload;
    try {
      cloned = JSON.parse(JSON.stringify(payload));
    } catch (error) {
      return { changed: false, data: payload };
    }

    const walk = (node) => {
      if (!node || typeof node !== "object") return;

      if (Array.isArray(node)) {
        node.forEach((item) => walk(item));
        return;
      }

      if (Array.isArray(node.selling_plan_groups)) {
        const groups = node.selling_plan_groups;
        const filteredGroups = this.selectSellingPlanGroups(groups);
        const isDifferent =
          Array.isArray(filteredGroups) &&
          (filteredGroups.length !== groups.length ||
            filteredGroups.some((group, index) => group !== groups[index]));

        if (isDifferent) {
          node.selling_plan_groups = filteredGroups;
          changed = true;
        }
      }

      Object.keys(node).forEach((key) => walk(node[key]));
    };

    walk(cloned);
    return { changed, data: cloned };
  }

  patchFetch() {
    if (this.fetchPatched || typeof window.fetch !== "function") return;
    this.fetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const request = args[0];
      const requestUrl =
        typeof request === "string"
          ? request
          : request && typeof request.url === "string"
            ? request.url
            : "";

      if (!this.shouldProcessUrl(requestUrl)) return response;

      const contentType = String(
        response?.headers?.get("content-type") || "",
      ).toLowerCase();
      if (!contentType.includes("application/json")) return response;

      try {
        const payload = await response.clone().json();
        const { changed, data } = this.filterPayload(payload);
        if (!changed) return response;

        const headers = new Headers(response.headers);
        headers.set("content-type", "application/json; charset=utf-8");

        return new Response(JSON.stringify(data), {
          status: response.status,
          statusText: response.statusText,
          headers,
        });
      } catch (error) {
        return response;
      }
    };
  }

  patchXhr() {
    if (this.xhrPatched || typeof XMLHttpRequest === "undefined") return;
    this.xhrPatched = true;

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
      this.__pdpSellingPlanFilterUrl = typeof url === "string" ? url : "";
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function send(...args) {
      this.addEventListener("readystatechange", () => {
        if (this.readyState !== 4) return;
        if (!window.__pdpSellingPlanFilterInstance) return;

        const instance = window.__pdpSellingPlanFilterInstance;
        if (!instance.shouldProcessUrl(this.__pdpSellingPlanFilterUrl)) return;

        const contentType = String(
          this.getResponseHeader("content-type") || "",
        ).toLowerCase();
        if (!contentType.includes("application/json")) return;

        let parsedPayload = null;
        let responseIsJsonType = false;

        try {
          if (
            this.responseType === "" ||
            this.responseType === "text" ||
            this.responseType === undefined
          ) {
            parsedPayload = JSON.parse(this.responseText || "{}");
          } else if (this.responseType === "json" && this.response) {
            parsedPayload = this.response;
            responseIsJsonType = true;
          }
        } catch (error) {
          parsedPayload = null;
        }

        if (!parsedPayload) return;

        const { changed, data } = instance.filterPayload(parsedPayload);
        if (!changed) return;

        const serialized = JSON.stringify(data);

        try {
          Object.defineProperty(this, "responseText", {
            configurable: true,
            get() {
              return serialized;
            },
          });
        } catch (error) {}

        try {
          Object.defineProperty(this, "response", {
            configurable: true,
            get() {
              return responseIsJsonType ? data : serialized;
            },
          });
        } catch (error) {}
      });

      return originalSend.apply(this, args);
    };
  }

  observe() {
    if (this.observer) return;

    this.observer = new MutationObserver(() => {
      if (this.scheduled) return;
      this.scheduled = true;
      requestAnimationFrame(() => {
        this.scheduled = false;
        this.apply();
      });
    });

    this.observer.observe(document.body, {
      subtree: true,
      childList: true,
    });
  }

  getAllRoots() {
    const roots = [];
    const visit = (root) => {
      roots.push(root);
      root.querySelectorAll("*").forEach((element) => {
        if (element.shadowRoot) {
          visit(element.shadowRoot);
        }
      });
    };
    visit(document);
    return roots;
  }

  getStayAiSelects(root) {
    if (!root || typeof root.querySelectorAll !== "function") return [];
    return Array.from(
      root.querySelectorAll(
        "select[name='stayai_selling_plan'], select.stayai-subscription-dropdown",
      ),
    );
  }

  ensureValidSelectState(select) {
    if (!select || !select.options.length) return;

    const options = Array.from(select.options).filter(
      (option) => !option.hidden && !option.disabled,
    );
    if (!options.length) return;

    const hasSelected = options.some((option) => option.selected);
    if (hasSelected) return;

    options[0].selected = true;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    select.dispatchEvent(new Event("input", { bubbles: true }));
  }

  filterDropdownOptions(select) {
    if (!select || !select.options || select.options.length === 0) return;

    const selectedBefore = String(select.value || "");
    const hasAllowedIds = this.allowedSellingPlanIds.size > 0;
    const allOptions = Array.from(select.options);
    const filteredOptions = [];
    const filteredOptionsByIdOnly = [];
    const seenLabels = new Set();

    allOptions.forEach((option) => {
      const optionId = String(option.value || "");
      const optionLabel = option.textContent ? option.textContent.trim() : "";
      const matchesFrequency = this.isAllowedFrequencyOption(optionLabel);
      const matchesAllowedId = hasAllowedIds
        ? this.allowedSellingPlanIds.has(optionId)
        : true;
      if (!matchesAllowedId) {
        return;
      }
      filteredOptionsByIdOnly.push(option);
      if (!matchesFrequency) return;
      const normalizedLabel = this.normalizeText(optionLabel) || optionId;
      if (seenLabels.has(normalizedLabel)) return;
      seenLabels.add(normalizedLabel);
      filteredOptions.push(option);
    });

    const keepOptions =
      filteredOptions.length > 0
        ? filteredOptions
        : filteredOptionsByIdOnly.length > 0
          ? filteredOptionsByIdOnly
          : allOptions;
    const keepSet = new Set(keepOptions);
    allOptions.forEach((option) => {
      if (!keepSet.has(option)) {
        option.remove();
      }
    });

    this.ensureValidSelectState(select);

    const selectedAfter = String(select.value || "");
    const selectionChanged = selectedBefore !== selectedAfter;
    if (selectionChanged) {
      select.dispatchEvent(new Event("change", { bubbles: true }));
      select.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  filterRoot(root) {
    const selects = this.getStayAiSelects(root);
    if (!selects.length) return;
    selects.forEach((select) => this.filterDropdownOptions(select));
  }

  apply() {
    if (this.isApplying) return;
    this.isApplying = true;

    try {
      this.getAllRoots().forEach((root) => this.filterRoot(root));
    } finally {
      this.isApplying = false;
    }
  }
}

const pdpSellingPlanFilter = new PdpSellingPlanFilter();
window.__pdpSellingPlanFilterInstance = pdpSellingPlanFilter;
pdpSellingPlanFilter.init();

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
